import {
  forwardRef,
  Tag as ChakraTag,
  TagCloseButton,
  TagProps as ChakraTagProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import Icon, { TypeIconName } from '../Icon/Icon';
import Skeleton from '../Skeleton/Skeleton';
import SkeletonBox from '../Skeleton/SkeletonBox';
import Text from '../Text/Text';
import Tooltip from '../Tooltip/Tooltip';
import { TypeColors } from '../../types/bitkit';

export interface TagProps extends Omit<ChakraTagProps, 'colorScheme' | 'size' | 'variant'> {
  closeButtonTooltip?: string;
  colorScheme?: 'neutral' | 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'purpleFilled';
  iconColor?: TypeColors;
  iconName?: TypeIconName;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  size?: 'sm' | 'md';
  withIcon?: boolean;
}

const Tag = forwardRef<TagProps, 'span'>((props, ref) => {
  const {
    children,
    closeButtonTooltip,
    colorScheme = 'neutral',
    iconColor,
    iconName,
    isDisabled,
    isLoading,
    onClose,
    size = 'md',
    withIcon,
    ...tagProps
  } = props;
  const style = useMultiStyleConfig('Tag', props);

  return (
    <ChakraTag
      aria-busy={isLoading}
      aria-disabled={isDisabled}
      colorScheme={colorScheme}
      data-disabled={isDisabled || undefined}
      data-loading={isLoading || undefined}
      size={size}
      sx={style.container}
      {...tagProps}
      ref={ref}
    >
      {(withIcon || !!iconName) && (
        <Icon __css={style.icon} color={iconColor} name={iconName || 'Check'} size={size === 'sm' ? '16' : '24'} />
      )}
      <Text as="span" hasEllipsis sx={style.label}>
        {isLoading ? (
          <Skeleton isActive>
            <SkeletonBox sx={style.skeleton} />
          </Skeleton>
        ) : (
          children
        )}
      </Text>
      {!!onClose && (
        <Tooltip isDisabled={!closeButtonTooltip} label={closeButtonTooltip}>
          <TagCloseButton as="span" isDisabled={isDisabled || isLoading} onClick={onClose}>
            <Icon name="Cross" size="16" />
          </TagCloseButton>
        </Tooltip>
      )}
    </ChakraTag>
  );
});

export default Tag;
